(() => {
  const imageCache = new Map();
  const hitCanvasCache = new Map();
  const storageKey = 'ozurNativeClubBat';
  const normalizeCapsQuantity = (value) => {
    const quantity = Number(value);
    return Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : 0;
  };
  const capsLabel = () => ({
    fr: 'Nombre de coiffes assorties',
    en: 'Number of matching caps',
    de: 'Anzahl passender Badekappen',
    es: 'Numero de gorros a juego',
  }[document.documentElement.lang.split('-')[0]] || 'Nombre de coiffes assorties');
  const loadImage = (src) => {
    if (imageCache.has(src)) return imageCache.get(src);
    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Image unavailable: ${src}`));
      image.src = src;
    });
    imageCache.set(src, promise);
    return promise;
  };
  const loadHitCanvas = async (src) => {
    if (hitCanvasCache.has(src)) return hitCanvasCache.get(src);
    const promise = loadImage(src).then((image) => {
      const hitCanvas = document.createElement('canvas');
      hitCanvas.width = 1000;
      hitCanvas.height = 1000;
      hitCanvas.getContext('2d').drawImage(image, 0, 0, 1000, 1000);
      return hitCanvas;
    });
    hitCanvasCache.set(src, promise);
    return promise;
  };
  const ascii = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E]/g, ' ')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
  const encodeDesign = (value) => {
    const bytes = new TextEncoder().encode(JSON.stringify(value));
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };
  const decodeDesign = (value) => {
    try {
      let normalized = value.replace(/-/g, '+').replace(/_/g, '/');
      normalized += '='.repeat((4 - (normalized.length % 4)) % 4);
      const binary = atob(normalized);
      const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (error) {
      return null;
    }
  };
  const bytesFromDataUrl = (dataUrl) => {
    const binary = atob(dataUrl.split(',')[1]);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  };
  const concatBytes = (parts) => {
    const length = parts.reduce((total, part) => total + part.length, 0);
    const output = new Uint8Array(length);
    let offset = 0;
    parts.forEach((part) => {
      output.set(part, offset);
      offset += part.length;
    });
    return output;
  };
  const createBatPdf = (data, labels) => {
    const encoder = new TextEncoder();
    const front = bytesFromDataUrl(data.previews.front);
    const back = bytesFromDataUrl(data.previews.back);
    const pdfColor = (hex) => {
      const value = /^#[0-9a-f]{6}$/i.test(hex || '') ? hex.slice(1) : 'FFFFFF';
      return [0, 2, 4].map((offset) => (Number.parseInt(value.slice(offset, offset + 2), 16) / 255).toFixed(3)).join(' ');
    };
    const headerTitle = `${data.club || labels.club} - MAILLOT ${data.model}`.toUpperCase();
    const contactLine = `${data.email}  |  ${data.phone}  |  ${labels.quantity}: ${data.quantity}  |  ${labels.reference}: ${data.reference}`;
    const commands = [
      '1 1 1 rg 0 0 842 595 re f',
      '0.125 0.110 0.115 rg 15 515 812 65 re f',
      '0.125 0.110 0.115 rg 15 0 812 20 re f',
      '0.125 0.110 0.115 RG 2 w 17 20 808 560 re S',
      '1 1 1 rg',
      'BT /F3 36 Tf 40 536 Td (OZUR) Tj ET',
      `BT /F3 12 Tf 270 548 Td (${ascii(headerTitle).slice(0, 46)}) Tj ET`,
      `BT /F1 5.5 Tf 270 530 Td (${ascii(contactLine).slice(0, 100)}) Tj ET`,
      '0.86 0.86 0.86 rg 620 533 180 23 re f',
      '0.08 0.08 0.08 rg',
      `BT /F1 8 Tf 630 542 Td (${ascii(data.date).slice(0, 32)}) Tj ET`,
      '0 0 0 rg',
      `BT /F1 9 Tf 40 498 Td (${ascii(capsLabel())}: ${normalizeCapsQuantity(data.capsQuantity)} - 20 EUR/piece) Tj ET`,
      'q 300 0 0 300 58 178 cm /Im1 Do Q',
      'q 300 0 0 300 484 178 cm /Im2 Do Q',
      '0 0 0 rg',
      `BT /F1 8 Tf 182 172 Td (${ascii(labels.front).toUpperCase()} / FRONT) Tj ET`,
      `BT /F1 8 Tf 611 172 Td (${ascii(labels.back).toUpperCase()} / BACK) Tj ET`,
      '0.15 0.15 0.15 RG 0.8 w 25 38 792 118 re S',
    ];
    data.zones.slice(0, 9).forEach((zone, index) => {
      const column = Math.floor(index / 3);
      const row = index % 3;
      const x = 38 + (column * 186);
      const y = 126 - (row * 34);
      commands.push('0 0 0 rg');
      commands.push(`BT /F1 6.5 Tf ${x} ${y} Td (${ascii(zone.name).toUpperCase().slice(0, 24)}) Tj ET`);
      commands.push(`BT /F1 5.8 Tf ${x + 105} ${y + 7} Td (${ascii(zone.colorName || zone.color).slice(0, 20)}) Tj ET`);
      commands.push(`${pdfColor(zone.color)} rg ${x + 148} ${y - 10} 20 20 re f`);
      commands.push('0.82 0.82 0.82 RG 0.4 w');
      commands.push(`${x} ${y - 16} m ${x + 172} ${y - 16} l S`);
    });
    data.options.slice(0, 3).forEach((option, index) => {
      const y = 126 - (index * 34);
      commands.push('0 0 0 rg');
      commands.push(`BT /F1 6.5 Tf 610 ${y} Td (${ascii(option.group).slice(0, 20)} :) Tj ET`);
      commands.push(`BT /F1 6.3 Tf 674 ${y} Td (${ascii(option.name).slice(0, 27)}) Tj ET`);
      commands.push('0.82 0.82 0.82 RG 0.4 w');
      commands.push(`610 ${y - 16} m 806 ${y - 16} l S`);
    });
    commands.push('1 1 1 rg BT /F1 6 Tf 30 7 Td (OZUR - shop.ozur-swimwear.com) Tj ET');
    const content = encoder.encode(commands.join('\n'));
    const objects = [
      encoder.encode('<< /Type /Catalog /Pages 2 0 R >>'),
      encoder.encode('<< /Type /Pages /Kids [3 0 R] /Count 1 >>'),
      encoder.encode('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 842 595] /Resources << /Font << /F1 5 0 R /F2 6 0 R /F3 9 0 R >> /XObject << /Im1 7 0 R /Im2 8 0 R >> >> /Contents 4 0 R >>'),
      concatBytes([encoder.encode(`<< /Length ${content.length} >>\nstream\n`), content, encoder.encode('\nendstream')]),
      encoder.encode('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>'),
      encoder.encode('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>'),
      concatBytes([encoder.encode(`<< /Type /XObject /Subtype /Image /Width 1000 /Height 1000 /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${front.length} >>\nstream\n`), front, encoder.encode('\nendstream')]),
      concatBytes([encoder.encode(`<< /Type /XObject /Subtype /Image /Width 1000 /Height 1000 /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${back.length} >>\nstream\n`), back, encoder.encode('\nendstream')]),
      encoder.encode('<< /Type /Font /Subtype /Type1 /BaseFont /Times-Roman >>'),
    ];
    const parts = [encoder.encode('%PDF-1.4\n%OZUR\n')];
    const offsets = [0];
    let total = parts[0].length;
    objects.forEach((object, index) => {
      offsets.push(total);
      const wrapped = concatBytes([encoder.encode(`${index + 1} 0 obj\n`), object, encoder.encode('\nendobj\n')]);
      parts.push(wrapped);
      total += wrapped.length;
    });
    const xrefOffset = total;
    const xref = [`xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`];
    offsets.slice(1).forEach((offset) => xref.push(`${String(offset).padStart(10, '0')} 00000 n \n`));
    xref.push(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);
    parts.push(encoder.encode(xref.join('')));
    return new Blob([concatBytes(parts)], { type: 'application/pdf' });
  };
  const initialize = (section) => {
    if (section.dataset.initialized === 'true') return;
    const dataElement = section.querySelector('[data-ozur-configurator-data]');
    if (!dataElement) return;
    section.dataset.initialized = 'true';
    const { models } = JSON.parse(dataElement.textContent);
    const labels = JSON.parse(section.querySelector('[data-configurator-labels]').textContent);
    const nativeLayout = section.querySelector('.club-configurator__native');
    const stageWrap = section.querySelector('.club-configurator__stage-wrap');
    const canvas = section.querySelector('[data-design-canvas]');
    const context = canvas.getContext('2d');
    const modelButtons = [...section.querySelectorAll('[data-model-key]')];
    const faceButtons = [...section.querySelectorAll('[data-face-index]')];
    const zonesElement = section.querySelector('[data-zone-list]');
    const accordion = section.querySelector('[data-configurator-accordion]');
    const palettePanel = section.querySelector('[data-palette-panel]');
    const paletteElement = section.querySelector('[data-palette]');
    const selectedZoneName = section.querySelector('[data-selected-zone-name]');
    const customColor = section.querySelector('[data-custom-color]');
    const patchesElement = section.querySelector('.club-configurator__patches');
    const patchCategories = {
      straps: section.querySelector('[data-patch-category="straps"]'),
      rhinestones: section.querySelector('[data-patch-category="rhinestones"]'),
    };
    const selectedModel = section.querySelector('[data-selected-model]');
    const modelField = section.querySelector('[data-model-field]');
    const referenceField = section.querySelector('[data-bat-reference-field]');
    const designField = section.querySelector('[data-design-field]');
    const designLinkField = section.querySelector('[data-design-link-field]');
    const batMessageField = section.querySelector('[data-bat-message-field]');
    const batDownloadStep = section.querySelector('[data-bat-download-step]');
    const quotePanel = section.querySelector('[data-quote-panel]');
    const form = section.querySelector('form');
    const quantityInput = section.querySelector('[data-total-quantity]');
    const capsQuantityInput = section.querySelector('[data-caps-quantity]');
    const status = section.querySelector('[data-configurator-status]');
    const batPanel = section.querySelector('[data-bat-panel]');
    const quoteSuccess = section.querySelector('[data-quote-success]');
    let currentKey = modelButtons[0]?.dataset.modelKey || Object.keys(models)[0];
    let currentFace = 0;
    let selectedZone = -1;
    let colors = [];
    let patches = {};
    let currentReference = '';
    let renderToken = 0;
    let hoverToken = 0;
    let hoveredZone = -1;
    const mobileLayout = window.matchMedia('(max-width: 749px)');
    const syncLayoutOrder = () => {
      if (!nativeLayout || !stageWrap || !accordion) return;
      if (mobileLayout.matches) nativeLayout.insertBefore(stageWrap, accordion);
      else nativeLayout.insertBefore(accordion, stageWrap);
    };
    mobileLayout.addEventListener('change', syncLayoutOrder);
    syncLayoutOrder();
    const globalStrapOptions = [];
    const knownStrapOptions = new Set();
    Object.values(models).forEach((model) => {
      model.patchGroups.filter((group) => /^bret/i.test(group.name)).forEach((group) => {
        group.options.forEach((option) => {
          const optionKey = `${option.id}:${option.src}`;
          if (knownStrapOptions.has(optionKey)) return;
          knownStrapOptions.add(optionKey);
          globalStrapOptions.push(option);
        });
      });
    });
    const currentModel = () => models[currentKey];
    const globalStrapGroup = () => ({
      id: 'global-straps',
      name: labels.straps,
      faceIds: [(currentModel().faces[1] || currentModel().faces[0]).id],
      options: globalStrapOptions,
    });
    const designState = () => ({
      v: 1,
      model: currentKey,
      colors,
      patches,
    });
    const compactOptionGroups = (model = currentModel()) => [
      ...model.patchGroups.filter((group) => !/^bret/i.test(group.name)),
      { id: 'global-straps' },
    ];
    const encodeCompactColor = (color, model = currentModel()) => {
      const normalized = String(color || '').toUpperCase();
      const paletteIndex = model.palette.findIndex((entry) => entry.value.toUpperCase() === normalized);
      return paletteIndex >= 0 ? paletteIndex.toString(36) : `x${normalized.replace('#', '')}`;
    };
    const decodeCompactColor = (token, zone, model) => {
      if (/^x[0-9a-f]{6}$/i.test(token || '')) return `#${token.slice(1).toUpperCase()}`;
      const paletteIndex = Number.parseInt(token, 36);
      return Number.isInteger(paletteIndex) && model.palette[paletteIndex]
        ? model.palette[paletteIndex].value
        : zone.color;
    };
    const dateFromReference = (reference) => {
      const match = /^OZUR-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})$/.exec(reference || '');
      if (!match) return new Date().toLocaleString(document.documentElement.lang || 'fr-FR');
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]));
      return date.toLocaleString(document.documentElement.lang || 'fr-FR');
    };
    const createCompactBatUrl = (data) => {
      const params = new URLSearchParams();
      const optionIds = compactOptionGroups().map((group) => patches[group.id] || '');
      params.set('bat', '2');
      params.set('m', currentKey);
      params.set('c', colors.map((color) => encodeCompactColor(color)).join('.'));
      if (optionIds.some(Boolean)) params.set('o', optionIds.join('.'));
      params.set('n', data.club);
      params.set('e', data.email);
      params.set('t', data.phone);
      params.set('q', data.quantity);
      params.set('cq', String(normalizeCapsQuantity(data.capsQuantity)));
      params.set('r', data.reference.replace(/^OZUR-/, ''));
      return `${window.location.origin}${window.location.pathname}?${params.toString()}#ozur-bat`;
    };
    const readCompactBat = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('bat') !== '2') return null;
      const modelKey = params.get('m') || '';
      const model = models[modelKey];
      if (!model) return null;
      const colorTokens = (params.get('c') || '').split('.');
      const restoredColors = model.zones.map((zone, index) => decodeCompactColor(colorTokens[index], zone, model));
      const optionTokens = (params.get('o') || '').split('.');
      const restoredPatches = {};
      compactOptionGroups(model).forEach((group, index) => {
        if (optionTokens[index]) restoredPatches[group.id] = optionTokens[index];
      });
      const referencePart = params.get('r') || '';
      const reference = referencePart.startsWith('OZUR-') ? referencePart : `OZUR-${referencePart}`;
      return {
        state: { v: 1, model: modelKey, colors: restoredColors, patches: restoredPatches },
        data: {
          reference,
          date: dateFromReference(reference),
          club: params.get('n') || '',
          email: params.get('e') || '',
          phone: params.get('t') || '',
          model: model.name,
          quantity: params.get('q') || '',
          capsQuantity: normalizeCapsQuantity(params.get('cq')),
          previews: null,
        },
      };
    };
    const selectedOptions = () => [
      ...currentModel().patchGroups.filter((group) => !/^bret/i.test(group.name)),
      globalStrapGroup(),
    ].map((group) => {
      const id = patches[group.id] || '';
      const option = group.options.find((entry) => entry.id === id);
      return option ? { group: group.name, id: option.id, name: option.name } : null;
    }).filter(Boolean);
    const drawFace = async (faceIndex, targetCanvas = canvas) => {
      const token = ++renderToken;
      const model = currentModel();
      const face = model.faces[faceIndex] || model.faces[0];
      const targetContext = targetCanvas.getContext('2d');
      targetContext.save();
      targetContext.setTransform(1, 0, 0, 1, 0, 0);
      targetContext.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
      targetContext.fillStyle = '#ffffff';
      targetContext.fillRect(0, 0, targetCanvas.width, targetCanvas.height);
      targetContext.restore();
      status.hidden = false;
      try {
        const base = await loadImage(face.base);
        if (targetCanvas === canvas && token !== renderToken) return;
        targetContext.drawImage(base, 0, 0, targetCanvas.width, targetCanvas.height);
        for (const layer of face.layers) {
          const image = await loadImage(layer.src);
          if (targetCanvas === canvas && token !== renderToken) return;
          const buffer = document.createElement('canvas');
          buffer.width = targetCanvas.width;
          buffer.height = targetCanvas.height;
          const bufferContext = buffer.getContext('2d');
          bufferContext.drawImage(image, 0, 0, buffer.width, buffer.height);
          bufferContext.globalCompositeOperation = 'source-in';
          bufferContext.fillStyle = colors[layer.zone] || '#ffffff';
          bufferContext.fillRect(0, 0, buffer.width, buffer.height);
          targetContext.drawImage(buffer, 0, 0);
        }
        if (targetCanvas === canvas && hoveredZone >= 0) {
          for (const layer of face.layers.filter((entry) => entry.zone !== hoveredZone)) {
            const image = await loadImage(layer.src);
            if (token !== renderToken) return;
            const dimmer = document.createElement('canvas');
            dimmer.width = targetCanvas.width;
            dimmer.height = targetCanvas.height;
            const dimmerContext = dimmer.getContext('2d');
            dimmerContext.drawImage(image, 0, 0, dimmer.width, dimmer.height);
            dimmerContext.globalCompositeOperation = 'source-in';
            dimmerContext.fillStyle = 'rgba(190, 190, 190, 0.35)';
            dimmerContext.fillRect(0, 0, dimmer.width, dimmer.height);
            targetContext.drawImage(dimmer, 0, 0);
          }
          for (const layer of face.layers.filter((entry) => entry.zone === hoveredZone)) {
            const image = await loadImage(layer.src);
            if (token !== renderToken) return;
            const focus = document.createElement('canvas');
            focus.width = targetCanvas.width;
            focus.height = targetCanvas.height;
            const focusContext = focus.getContext('2d');
            focusContext.drawImage(image, 0, 0, focus.width, focus.height);
            focusContext.globalCompositeOperation = 'source-in';
            focusContext.fillStyle = colors[layer.zone] || '#ffffff';
            focusContext.fillRect(0, 0, focus.width, focus.height);
            targetContext.drawImage(focus, 0, 0);
          }
        }
        for (const group of model.patchGroups.filter((entry) => !/^bret/i.test(entry.name))) {
          if (!group.faceIds.includes(face.id)) continue;
          const option = group.options.find((entry) => entry.id === patches[group.id]);
          if (option) targetContext.drawImage(await loadImage(option.src), 0, 0, targetCanvas.width, targetCanvas.height);
        }
        const strapGroup = globalStrapGroup();
        if (strapGroup.faceIds.includes(face.id)) {
          const strapOption = strapGroup.options.find((entry) => entry.id === patches[strapGroup.id]);
          if (strapOption) targetContext.drawImage(await loadImage(strapOption.src), 0, 0, targetCanvas.width, targetCanvas.height);
        }
      } finally {
        if (targetCanvas !== canvas || token === renderToken) status.hidden = true;
      }
    };
    const newReference = () => {
      const now = new Date();
      return `OZUR-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    };
    const collectBatData = async (reference = newReference()) => {
      const now = new Date();
      const previewCanvas = document.createElement('canvas');
      previewCanvas.width = 1000;
      previewCanvas.height = 1000;
      await drawFace(0, previewCanvas);
      const front = previewCanvas.toDataURL('image/jpeg', 0.88);
      await drawFace(1, previewCanvas);
      const back = previewCanvas.toDataURL('image/jpeg', 0.88);
      return {
        reference,
        date: now.toLocaleString(document.documentElement.lang || 'fr-FR'),
        club: form.elements.namedItem('contact[Nom du club]')?.value || '',
        email: form.elements.namedItem('contact[email]')?.value || '',
        phone: form.elements.namedItem('contact[Téléphone]')?.value || '',
        model: currentModel().name,
        quantity: quantityInput.value,
        capsQuantity: normalizeCapsQuantity(capsQuantityInput?.value),
        zones: currentModel().zones.map((zone, index) => ({
          name: zone.name,
          color: colors[index],
          colorName: currentModel().palette.find((entry) => entry.value.toUpperCase() === colors[index].toUpperCase())?.name || colors[index],
        })),
        options: selectedOptions(),
        previews: { front, back },
      };
    };
    const downloadBatFile = async (data) => {
      if (!data.previews) {
        const completedData = await collectBatData(data.reference);
        data = { ...completedData, ...data, previews: completedData.previews };
      }
      const blob = createBatPdf(data, labels);
      const link = document.createElement('a');
      const club = ascii(data.club).replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '') || 'Club';
      link.href = URL.createObjectURL(blob);
      link.download = `Maillot-personnalise-OZUR-${club}-${data.reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    };
    const renderZones = () => {
      const model = currentModel();
      zonesElement.replaceChildren();
      model.zones.forEach((zone, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `ozur-zone${index === selectedZone ? ' is-active' : ''}`;
        button.dataset.zoneIndex = index;
        button.setAttribute('aria-pressed', String(index === selectedZone));
        const swatch = document.createElement('span');
        swatch.className = 'ozur-zone__swatch';
        swatch.style.setProperty('--zone-color', colors[index]);
        const name = document.createElement('span');
        name.textContent = zone.name;
        const value = document.createElement('small');
        value.textContent = colors[index];
        button.append(swatch, name, value);
        zonesElement.appendChild(button);
      });
    };
    const renderPalette = () => {
      const model = currentModel();
      paletteElement.replaceChildren();
      const zone = model.zones[selectedZone];
      palettePanel.hidden = !zone;
      customColor.disabled = !zone;
      selectedZoneName.textContent = zone?.name || '';
      if (!zone) return;
      model.palette.forEach((color) => {
        const button = document.createElement('button');
        button.type = 'button';
        const active = color.value.toUpperCase() === colors[selectedZone].toUpperCase();
        button.className = `ozur-color${active ? ' is-active' : ''}`;
        button.style.setProperty('--swatch-color', color.value);
        button.title = color.name;
        button.setAttribute('aria-label', color.name);
        button.setAttribute('aria-pressed', String(active));
        button.dataset.color = color.value;
        paletteElement.appendChild(button);
      });
      customColor.value = colors[selectedZone] || '#ffffff';
    };
    const renderPatches = () => {
      const model = currentModel();
      const strapGroups = [globalStrapGroup()];
      const strassGroups = model.patchGroups.filter((group) => !/^bret/i.test(group.name));
      [
        { element: patchCategories.straps, groups: strapGroups },
        { element: patchCategories.rhinestones, groups: strassGroups },
      ].forEach((category) => {
        const menuContent = category.element;
        if (!menuContent) return;
        menuContent.replaceChildren();
        if (!category.groups.length) {
          const emptyMessage = document.createElement('p');
          emptyMessage.className = 'ozur-option-menu__empty';
          emptyMessage.textContent = labels.noOptions;
          menuContent.appendChild(emptyMessage);
        }
        category.groups.forEach((group) => {
          const subgroup = document.createElement('div');
          subgroup.className = 'ozur-patch';
          if (category.groups.length > 1) {
            const subgroupTitle = document.createElement('p');
            subgroupTitle.className = 'ozur-patch__title';
            subgroupTitle.textContent = group.name;
            subgroup.appendChild(subgroupTitle);
          }
          const gallery = document.createElement('div');
          gallery.className = 'ozur-patch__gallery';
          const emptyOption = document.createElement('button');
          emptyOption.type = 'button';
          emptyOption.className = `ozur-patch__option${patches[group.id] ? '' : ' is-active'}`;
          emptyOption.dataset.patchGroup = group.id;
          emptyOption.dataset.patchId = '';
          emptyOption.setAttribute('aria-pressed', String(!patches[group.id]));
          const emptyVisual = document.createElement('span');
          emptyVisual.className = 'ozur-patch__empty';
          emptyVisual.setAttribute('aria-hidden', 'true');
          emptyVisual.textContent = '×';
          const emptyLabel = document.createElement('span');
          emptyLabel.textContent = labels.none;
          emptyOption.append(emptyVisual, emptyLabel);
          gallery.appendChild(emptyOption);
          group.options.forEach((option) => {
            const optionElement = document.createElement('button');
            optionElement.type = 'button';
            optionElement.className = `ozur-patch__option${patches[group.id] === option.id ? ' is-active' : ''}`;
            optionElement.dataset.patchGroup = group.id;
            optionElement.dataset.patchId = option.id;
            optionElement.setAttribute('aria-pressed', String(patches[group.id] === option.id));
            const image = document.createElement('img');
            image.src = option.src;
            image.alt = '';
            image.loading = 'lazy';
            image.width = 130;
            image.height = 130;
            const optionLabel = document.createElement('span');
            optionLabel.textContent = option.name;
            optionElement.append(image, optionLabel);
            gallery.appendChild(optionElement);
          });
          subgroup.appendChild(gallery);
          menuContent.appendChild(subgroup);
        });
      });
    };
    const renderControls = () => {
      renderZones();
      renderPalette();
      renderPatches();
    };
    const selectZone = (zoneIndex) => {
      if (!Number.isInteger(zoneIndex) || !currentModel().zones[zoneIndex]) return;
      selectedZone = zoneIndex;
      renderZones();
      renderPalette();
    };
    const hitTestZone = async (clientX, clientY) => {
      const bounds = canvas.getBoundingClientRect();
      const x = Math.max(0, Math.min(999, Math.floor((clientX - bounds.left) * (1000 / bounds.width))));
      const y = Math.max(0, Math.min(999, Math.floor((clientY - bounds.top) * (1000 / bounds.height))));
      const face = currentModel().faces[currentFace] || currentModel().faces[0];
      for (const layer of [...face.layers].reverse()) {
        try {
          const hitCanvas = await loadHitCanvas(layer.src);
          const alpha = hitCanvas.getContext('2d', { willReadFrequently: true }).getImageData(x, y, 1, 1).data[3];
          if (alpha > 20) return layer.zone;
        } catch (error) {
          canvas.style.cursor = 'pointer';
          return selectedZone;
        }
      }
      return -1;
    };
    const applyModel = (key, restoredState = null) => {
      if (!models[key]) return;
      currentKey = key;
      currentFace = 0;
      hoveredZone = -1;
      selectedZone = -1;
      colors = currentModel().zones.map((zone, index) => restoredState?.colors?.[index] || zone.color);
      patches = restoredState?.patches || {};
      selectedModel.textContent = currentModel().name;
      modelField.value = currentModel().name;
      modelButtons.forEach((button) => {
        const active = button.dataset.modelKey === currentKey;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      faceButtons.forEach((button, index) => button.classList.toggle('is-active', index === 0));
      renderControls();
      drawFace(0);
    };
    modelButtons.forEach((button) => button.addEventListener('click', () => {
      applyModel(button.dataset.modelKey);
      section.querySelector('[data-configurator-workspace]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
    faceButtons.forEach((button) => button.addEventListener('click', () => {
      currentFace = Number(button.dataset.faceIndex);
      hoveredZone = -1;
      faceButtons.forEach((item) => item.classList.toggle('is-active', item === button));
      drawFace(currentFace);
    }));
    zonesElement.addEventListener('click', (event) => {
      const button = event.target.closest('[data-zone-index]');
      if (!button) return;
      selectZone(Number(button.dataset.zoneIndex));
    });
    paletteElement.addEventListener('click', (event) => {
      const button = event.target.closest('[data-color]');
      if (!button || selectedZone < 0) return;
      colors[selectedZone] = button.dataset.color;
      renderZones();
      renderPalette();
      drawFace(currentFace);
    });
    customColor.addEventListener('change', () => {
      if (selectedZone < 0) return;
      colors[selectedZone] = customColor.value.toUpperCase();
      renderZones();
      renderPalette();
      drawFace(currentFace);
    });
    patchesElement.addEventListener('click', (event) => {
      const button = event.target.closest('[data-patch-id]');
      if (!button) return;
      patches[button.dataset.patchGroup] = button.dataset.patchId;
      patchesElement.querySelectorAll(`[data-patch-group="${button.dataset.patchGroup}"]`).forEach((item) => {
        const active = item.dataset.patchId === button.dataset.patchId;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      const selectedGroup = button.dataset.patchGroup === 'global-straps'
        ? globalStrapGroup()
        : currentModel().patchGroups.find((group) => group.id === button.dataset.patchGroup);
      const targetFaceIndex = currentModel().faces.findIndex((face) => selectedGroup?.faceIds.includes(face.id));
      if (targetFaceIndex >= 0) {
        currentFace = targetFaceIndex;
        hoveredZone = -1;
        faceButtons.forEach((item, index) => item.classList.toggle('is-active', index === currentFace));
      }
      drawFace(currentFace);
    });
    canvas.addEventListener('click', async (event) => {
      const zoneIndex = await hitTestZone(event.clientX, event.clientY);
      if (zoneIndex >= 0) selectZone(zoneIndex);
    });
    canvas.addEventListener('pointermove', (event) => {
      const token = ++hoverToken;
      const { clientX, clientY } = event;
      window.requestAnimationFrame(async () => {
        const zoneIndex = await hitTestZone(clientX, clientY);
        if (token !== hoverToken) return;
        canvas.style.cursor = zoneIndex >= 0 ? 'pointer' : 'default';
        if (zoneIndex !== hoveredZone) {
          hoveredZone = zoneIndex;
          drawFace(currentFace);
        }
      });
    });
    canvas.addEventListener('pointerleave', () => {
      hoverToken += 1;
      canvas.style.cursor = 'default';
      if (hoveredZone >= 0) {
        hoveredZone = -1;
        drawFace(currentFace);
      }
    });
    accordion?.querySelectorAll('details').forEach((menu) => {
      menu.addEventListener('toggle', () => {
        if (!menu.open) return;
        accordion.querySelectorAll('details').forEach((otherMenu) => {
          if (otherMenu !== menu) otherMenu.open = false;
        });
      });
    });
    section.querySelector('[data-reset-configurator]')?.addEventListener('click', () => applyModel(currentKey));
    section.querySelectorAll('[data-open-bat-step]').forEach((button) => button.addEventListener('click', () => batDownloadStep.scrollIntoView({ behavior: 'smooth', block: 'start' })));
    section.querySelector('[data-download-current-bat]')?.addEventListener('click', async (event) => {
      if (capsQuantityInput && !capsQuantityInput.reportValidity()) return;
      const button = event.currentTarget;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        currentReference = currentReference || newReference();
        referenceField.value = currentReference;
        const data = await collectBatData(currentReference);
        await downloadBatFile(data);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    });
    form?.addEventListener('submit', (event) => {
      if (!form.reportValidity()) {
        event.preventDefault();
        return;
      }

      const reference = currentReference || newReference();
      currentReference = reference;
      const state = designState();
      const data = {
        reference,
        date: new Date().toLocaleString(document.documentElement.lang || 'fr-FR'),
        club: form.elements.namedItem('contact[Nom du club]')?.value || '',
        email: form.elements.namedItem('contact[email]')?.value || '',
        phone: form.elements.namedItem('contact[Téléphone]')?.value || '',
        model: currentModel().name,
        quantity: quantityInput.value,
        capsQuantity: normalizeCapsQuantity(capsQuantityInput?.value),
        designState: state,
        zones: currentModel().zones.map((zone, index) => ({
          name: zone.name,
          color: colors[index],
          colorName: currentModel().palette.find(
            (entry) => entry.value.toUpperCase() === colors[index].toUpperCase()
          )?.name || colors[index],
        })),
        options: selectedOptions(),
        previews: null,
      };

      const reconstructionUrl = createCompactBatUrl(data);
      referenceField.value = reference;
      designField.value = JSON.stringify(state);
      designLinkField.value = reconstructionUrl;
      batMessageField.value = `${labels.batEmailLink}\n${reconstructionUrl}`;

      try {
        sessionStorage.setItem(storageKey, JSON.stringify(data));
        sessionStorage.setItem('ozurClubQuoteKlaviyo:' + window.location.pathname, JSON.stringify({
          id: crypto.randomUUID(),
          createdAt: Date.now(),
          email: data.email,
          properties: {
            club: data.club,
            modele: data.model,
            nombre_maillots: Number(data.quantity),
            nombre_coiffes: data.capsQuantity,
            prix_unitaire_coiffe: 20,
            reference,
            lien_bat: reconstructionUrl,
          },
        }));
      } catch (error) {
        console.warn('OZUR : sauvegarde locale indisponible.', error);
      }
    });

    const compactBat = readCompactBat();
    const restored = compactBat?.state || decodeDesign(new URLSearchParams(window.location.search).get('ozur_design') || '');
    applyModel(restored?.model || currentKey, restored);
    let batData = null;
    if (compactBat) {
      batData = compactBat.data;
      const restoredFields = {
        'contact[Nom du club]': batData.club,
        'contact[email]': batData.email,
        'contact[Téléphone]': batData.phone,
        'contact[Nombre de maillots]': batData.quantity,
        'contact[Nombre de coiffes assorties (20 EUR pièce)]': normalizeCapsQuantity(batData.capsQuantity),
      };
      Object.entries(restoredFields).forEach(([name, value]) => {
        const field = form.elements.namedItem(name);
        if (field) field.value = value;
      });
      referenceField.value = batData.reference;
      designField.value = JSON.stringify(restored);
      modelField.value = currentModel().name;
    } else if (quoteSuccess) {
      try { batData = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch (error) { batData = null; }
      if (batData?.designState) {
        applyModel(batData.designState.model, batData.designState);
      }
    }
    if (!batData && window.location.hash.startsWith('#ozur_bat=')) {
      batData = decodeDesign(window.location.hash.slice('#ozur_bat='.length));
    }
    if (batData && batPanel) {
      batPanel.hidden = false;
      if (compactBat && window.location.hash === '#ozur-bat') {
        window.requestAnimationFrame(() => batPanel.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      }
    }
    section.querySelector('[data-download-bat]')?.addEventListener('click', async () => {
      if (!batData) return;
      await downloadBatFile(batData);
    });
  };
  const initializeAll = (root = document) => root.querySelectorAll('.club-configurator').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();

(() => {
  const storageKey = 'ozurClubQuoteKlaviyo:' + window.location.pathname;
  let sending = false;

  const readPending = () => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    } catch (error) {
      return null;
    }
  };

  const sendConfirmedRequest = async () => {
    if (sending || !document.querySelector('#ClubConfiguratorQuote [data-quote-success]')) return;
    const pending = readPending();
    if (!pending || !pending.email || !pending.id) return;
    if (Date.now() - pending.createdAt > 30 * 60 * 1000) return;

    sending = true;
    try {
      const response = await fetch('https://a.klaviyo.com/client/events/?company_id=StaBvH', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: '2026-07-15',
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              unique_id: pending.id,
              properties: pending.properties,
              metric: {
                data: {
                  type: 'metric',
                  attributes: { name: 'Demande de devis club' },
                },
              },
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: pending.email },
                },
              },
            },
          },
        }),
      });

      if (!response.ok) throw new Error('Klaviyo HTTP ' + response.status);
      if (readPending()?.id === pending.id) sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('OZUR : transmission à Klaviyo non confirmée.', error);
    } finally {
      sending = false;
    }
  };

  const initialize = () => {
    const form = document.getElementById('ClubConfiguratorQuote');
    if (!form || form.dataset.ozurKlaviyoBound === 'true') return;
    form.dataset.ozurKlaviyoBound = 'true';

    form.addEventListener('formdata', (event) => {
      const fields = event.formData;
      const email = String(fields.get('contact[email]') || '').trim();
      const reference = String(fields.get('contact[Référence maillot personnalisé]') || '');
      const batUrl = String(fields.get('contact[Lien de reconstruction]') || '');
      const quantity = Number(fields.get('contact[Nombre de maillots]'));
      const capsQuantity = Number(form.querySelector('[data-caps-quantity]')?.value || 0);

      if (!email || !reference || !batUrl) return;
      if (!Number.isSafeInteger(quantity) || quantity < 8) return;
      if (!Number.isSafeInteger(capsQuantity) || capsQuantity < 0) return;

      try {
        const url = new URL(batUrl);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
        const pending = {
          id: crypto.randomUUID(),
          createdAt: Date.now(),
          email,
          properties: {
            club: String(fields.get('contact[Nom du club]') || ''),
            modele: String(fields.get('contact[Modèle]') || ''),
            nombre_maillots: quantity,
            nombre_coiffes: capsQuantity,
            prix_unitaire_coiffe: 20,
            reference,
            lien_bat: batUrl,
          },
        };
        sessionStorage.setItem(storageKey, JSON.stringify(pending));
      } catch (error) {
        console.warn('OZUR : préparation de la confirmation indisponible.', error);
      }
    });

    sendConfirmedRequest();
    window.addEventListener('online', sendConfirmedRequest);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', initialize);
})();

(() => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('bat') !== '2') return;

  const showPreview = () => {
    const section = document.querySelector('.club-configurator');
    const preview = section?.querySelector('.club-configurator__stage-wrap');
    if (!preview) return;

    if (window.location.hash === '#ozur-bat') {
      const url = new URL(window.location.href);
      url.hash = 'configurateur-club';
      history.replaceState(history.state, '', url.toString());
    }

    window.setTimeout(() => {
      preview.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  if (document.readyState === 'complete') {
    showPreview();
  } else {
    window.addEventListener('load', showPreview, { once: true });
  }
})();
